import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatNumber } from '@/shared/lib/format';

import type { ReportFilterChanges } from '../lib/report-filters';
import { GROUPING_LABEL } from '../lib/report-labels';
import type { OperationalReport } from '../types/report.types';
import { GroupLabel } from './filter-value';

const pct = (value: number | null) => (value === null ? '—' : `${value}%`);

export function OperationalTables({
  report,
  onFilter,
}: {
  report: OperationalReport;
  /** Applies the filter a row's channel, provider, brand or denomination label stands for. */
  onFilter?: (changes: ReportFilterChanges) => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-sm">
            Orders by {GROUPING_LABEL[report.groupBy].toLowerCase()}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{GROUPING_LABEL[report.groupBy]}</TableHead>
                <TableHead className="text-end">Orders</TableHead>
                <TableHead className="text-end">Completed</TableHead>
                <TableHead className="text-end">Failed</TableHead>
                <TableHead className="text-end">Processing</TableHead>
                <TableHead className="text-end">Success rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>
                    <GroupLabel
                      grouping={report.groupBy}
                      rowKey={row.key}
                      label={row.label}
                      onFilter={onFilter}
                    />
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatNumber(row.orders)}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatNumber(row.completed)}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatNumber(row.failed)}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">
                    {formatNumber(row.processing)}
                  </TableCell>
                  <TableCell className="text-end tabular-nums">{pct(row.successRatePct)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>Total</TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatNumber(report.totals.orders)}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatNumber(report.totals.completed)}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatNumber(report.totals.failed)}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {formatNumber(report.totals.processing)}
                </TableCell>
                <TableCell className="text-end tabular-nums">
                  {pct(report.totals.successRatePct)}
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Top failure reasons</CardTitle>
        </CardHeader>
        <CardContent>
          {report.failureReasons.length === 0 ? (
            <p className="text-muted-foreground text-sm">No failed orders in this period.</p>
          ) : (
            <ul className="divide-y text-sm">
              {report.failureReasons.map((reason) => (
                <li key={reason.code ?? 'none'} className="flex justify-between gap-2 py-1.5">
                  <span className="font-mono text-xs">{reason.code ?? 'No code recorded'}</span>
                  <span className="tabular-nums">{formatNumber(reason.count)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
